import { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { todayIso } from '../utils/dates';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

interface MonthPickerPopoverProps {
  /** Currently-displayed year in the calendar. */
  viewYear: number;
  /** Currently-displayed month in the calendar (0-based). */
  viewMonth: number;
  /** Called with the picked date; the calendar jumps to that month. */
  onSelect: (year: number, month0: number, day: number) => void;
}

/** gcal-style month calendar popover for jumping the calendar to any date. */
export function MonthPickerPopover({ viewYear, viewMonth, onSelect }: MonthPickerPopoverProps) {
  const [open, setOpen] = useState(false);
  const [py, setPy] = useState(viewYear);
  const [pm, setPm] = useState(viewMonth); // 0-based
  const ref = useRef<HTMLDivElement>(null);

  // Reseed the popover to the calendar's current month each time it opens.
  useEffect(() => {
    if (open) { setPy(viewYear); setPm(viewMonth); }
  }, [open, viewYear, viewMonth]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const prev = () => setPm(m => { if (m === 0) { setPy(y => y - 1); return 11; } return m - 1; });
  const next = () => setPm(m => { if (m === 11) { setPy(y => y + 1); return 0; } return m + 1; });

  const firstDow = new Date(py, pm, 1).getDay();
  const daysInMonth = new Date(py, pm + 1, 0).getDate();
  const today = todayIso();
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isoFor = (d: number) => `${py}-${String(pm + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Jump to date"
        className="p-1.5 rounded-full border border-nightshade-200 dark:border-nightshade-700 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors"
      >
        <CalendarDays className="w-4 h-4 text-nightshade-600 dark:text-nightshade-300" />
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-2 w-64 rounded-2xl border border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900 shadow-xl p-3">
          {/* Header: month + year with prev/next */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-nightshade-900 dark:text-nightshade-100">{MONTHS[pm]} {py}</span>
            <div className="flex items-center gap-1">
              <button onClick={prev} className="p-1 rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors">
                <ChevronLeft className="w-4 h-4 text-nightshade-500 dark:text-nightshade-400" />
              </button>
              <button onClick={next} className="p-1 rounded-full hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors">
                <ChevronRight className="w-4 h-4 text-nightshade-500 dark:text-nightshade-400" />
              </button>
            </div>
          </div>

          {/* Weekday labels */}
          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {DOW.map((d, i) => (
              <div key={i} className="text-center text-[10px] font-medium text-nightshade-400 dark:text-nightshade-500">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d === null) return <div key={i} />;
              const isToday = isoFor(d) === today;
              return (
                <button
                  key={i}
                  onClick={() => { onSelect(py, pm, d); setOpen(false); }}
                  className={`h-7 w-7 mx-auto flex items-center justify-center rounded-full text-xs transition-colors ${
                    isToday
                      ? 'bg-blueberry-600 text-white font-semibold'
                      : 'text-nightshade-700 dark:text-nightshade-300 hover:bg-blueberry-50 dark:hover:bg-blueberry-900/30'
                  }`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
